/**
 * Dataset Add Page - Create New Dataset
 * Upload CSV or JSON files to create evaluation datasets
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CloudUpload as CloudUploadIcon,
  ArrowBack as ArrowBackIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { datasetService } from '../../services/DatasetService';
import { ROUTE_PATHS } from '../../constants/routePaths';

export function DatasetAddPage() {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name must be less than 100 characters'),
    description: Yup.string()
      .required('Description is required')
      .min(10, 'Description must be at least 10 characters')
      .max(500, 'Description must be less than 500 characters'),
    type: Yup.string()
      .required('Type is required')
      .oneOf(['singleturn', 'multiturn'], 'Invalid type'),
    file: Yup.mixed()
      .required('File is required')
      .test('fileSize', 'File exceeds 50MB', (value) => {
        if (!value) return false;
        return (value as File).size <= 50 * 1024 * 1024; // 50MB
      })
      .test('fileType', 'Only CSV and JSON files are supported', (value) => {
        if (!value) return false;
        const file = value as File;
        const validTypes = ['text/csv', 'application/json'];
        const validExtensions = ['.csv', '.json'];
        return validTypes.includes(file.type) || validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      }),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      type: 'singleturn' as 'singleturn' | 'multiturn',
      file: null as File | null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!values.file) return;
      
      setIsUploading(true);
      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', values.file);
        formData.append('name', values.name);
        formData.append('description', values.description);
        formData.append('type', values.type);

        toast.info('Creating dataset...');
        
        // Call the API
        await datasetService.createDataset(formData);

        toast.success('Dataset created successfully');
        formik.resetForm();
        
        // Navigate back to datasets list page
        setTimeout(() => {
          navigate(ROUTE_PATHS.DATASETS);
        }, 1500);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to create dataset';
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
  });

  const handleFileSelect = async (file: File) => {
    await formik.setFieldValue('file', file);
    // Don't mark as touched immediately - let validation complete first
    setTimeout(() => {
      formik.setFieldTouched('file', false);
    }, 0);
  };

  const handleRemoveFile = () => {
    formik.setFieldValue('file', null);
    formik.setFieldTouched('file', false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]); // Only take the first file
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]); // Only take the first file
    }
  };

  const getFileSize = () => {
    if (!formik.values.file) return '0';
    return (formik.values.file.size / 1024 / 1024).toFixed(2);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'auto', py: 1.5 }}>
      <Container maxWidth="lg" sx={{ py: 0 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
            Create New Dataset
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Upload a CSV or JSON file to create an evaluation dataset
          </Typography>
        </Box>

        {/* Create Dataset Form */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', p: 2.5 }}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Name Field */}
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Dataset Name"
                placeholder="Enter dataset name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#94a3b8',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563eb',
                    },
                  },
                }}
              />

              {/* Description Field */}
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                placeholder="Enter dataset description"
                multiline
                rows={2}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#94a3b8',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563eb',
                    },
                  },
                }}
              />

              {/* Type Field */}
              <FormControl 
                fullWidth 
                error={formik.touched.type && Boolean(formik.errors.type)}
              >
                <InputLabel id="type-label">Dataset Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={formik.values.type}
                  label="Dataset Type"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="singleturn">Single Turn</MenuItem>
                  <MenuItem value="multiturn">Multi-turn</MenuItem>
                </Select>
                {formik.touched.type && formik.errors.type && (
                  <FormHelperText>{formik.errors.type}</FormHelperText>
                )}
              </FormControl>

              {/* File Upload */}
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#0f172a' }}>
                  Upload File
                </Typography>
                <input
                  id="file-upload"
                  type="file"
                  accept=".csv,.json"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
                <label htmlFor="file-upload">
                  <Paper
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    sx={{
                      p: 3,
                      border: `2px dashed ${
                        formik.touched.file && formik.errors.file
                          ? '#dc2626'
                          : '#cbd5e1'
                      }`,
                      borderRadius: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: formik.values.file ? '#f8fafc' : 'transparent',
                      '&:hover': {
                        bgcolor: '#f8fafc',
                        borderColor: '#94a3b8',
                      }
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 36, color: '#64748b', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {formik.values.file
                        ? formik.values.file.name
                        : 'Drop file here or click to browse'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Supports CSV and JSON formats (max 50MB)
                    </Typography>
                    {formik.values.file && (
                      <Typography variant="caption" sx={{ color: '#16a34a', display: 'block', mt: 1 }}>
                        Size: {getFileSize()} MB
                      </Typography>
                    )}
                  </Paper>
                </label>
                {formik.touched.file && formik.errors.file && (
                  <Typography variant="caption" sx={{ color: '#dc2626', mt: 1, display: 'block' }}>
                    {typeof formik.errors.file === 'string' 
                      ? formik.errors.file 
                      : 'Please select a valid file'}
                  </Typography>
                )}
              </Box>

              {/* Selected File Display */}
              {formik.values.file && (
                <Box>
                  <Chip
                    icon={<InsertDriveFileIcon />}
                    label={`${formik.values.file.name} (${getFileSize()} MB)`}
                    onDelete={handleRemoveFile}
                    sx={{
                      bgcolor: '#eff6ff',
                      '& .MuiChip-deleteIcon': {
                        color: '#64748b',
                        '&:hover': {
                          color: '#dc2626',
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 0 }}>
                <Button 
                  onClick={() => navigate(ROUTE_PATHS.DATASETS)}
                  disabled={isUploading}
                  sx={{ color: '#64748b' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isUploading}
                  sx={{ 
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' }
                  }}
                  startIcon={isUploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                >
                  {isUploading ? 'Creating...' : 'Create Dataset'}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
